<template>
  <div>
    <v-container class="pt-8 px-10">
      <h1 class="display-1 mb-4">Pages</h1>
      <v-row class="px-4 mb-4" align-center>
        <v-btn
          class="primary mr-3"
          @click="addChild"
          :disabled="!selectedId"
        >
          <v-icon class="mr-1">mdi-plus</v-icon>Add Child
        </v-btn>
        <v-btn
          class="primary"
          @click="deleteSelectedItem"
          :disabled="!selectedId || selectedId === '/'"
        >
          <v-icon class="mr-2">mdi-delete</v-icon>Delete
        </v-btn>
      </v-row>
      <v-row no-gutters>
        <v-col cols="12" sm="6" class="pr-8">
          <v-treeview
            ref="tree"
            dense
            hoverable
            activatable
            open-all
            :items="items"
            @update:active="onSelectTreeNode" >
            <template v-slot:append="{ item }">
              <v-icon v-if="item.page">mdi-file</v-icon>
            </template>
            <template v-slot:label="{ item }">
              {{ item.name || '(blank!)' }}
            </template>
          </v-treeview>
        </v-col>
        <v-col cols="12" sm="6">
          <v-card
            class="px-6 py-4"
            v-if="selectedId != null && selectedId !== '/'"
          >
            <p><span class="green--text">https://</span>{{selectedId}}</p>
            <v-text-field
              outlined
              persistent-hint
              :value="selectedId.split('/').reverse()[0]"
              @input="onChangePathName"
              hint="Duplicate path names not allowed. Invalid chars: &?/"
              label="Path name" />
            <div v-if="selectedItemPage">
              <v-img
                class="my-2 mx-8 primary"
                v-if="selectedItemPage.thumbnail"
                :src="selectedItemPage.thumbnail"
                style="border-radius: 8px;"
                :aspect-ratio="pageThumbnailAspectRatio" />
              <v-row no-gutters>
                <v-btn
                  class="mx-auto"
                  :color="selectedItemPage.thumbnail ? 'error' : 'primary'"
                  @click="onClickAddThumbnail"
                  outlined
                >
                  <v-icon left>mdi-image</v-icon>
                  {{ selectedItemPage.thumbnail ? 'Remove' : 'Add' }} thumbnail
                </v-btn>
              </v-row>
              <v-text-field
                class="mt-4"
                outlined
                hide-details
                v-model="selectedItemPage.title"
                label="Title" />
              <v-textarea
                class="mt-4"
                outlined
                hide-details
                no-resize
                v-model="selectedItemPage.description"
                label="Description" />
              <v-text-field
                class="mt-4"
                outlined
                hide-details
                v-model="selectedItemPage.scene"
                label="Scene reference" />
            </div>
            <v-row class="mt-4 mb-4" v-else>
              <v-btn
                class="primary mx-auto"
                @click="newPage"
              >
                <v-icon left>mdi-file</v-icon>New page
              </v-btn>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
    <ImageUploadDialog 
      ref="imageUploadDlg"
      title="Upload thumbnail"
      :aspect-ratio="pageThumbnailAspectRatio"
      @upload="url => $set(selectedItemPage, 'thumbnail', url)" />
  </div>
</template>

<script>
import ImageUploadDialog from '../components/ImageUploadDialog'

import { randomId } from '../utils'

export default {
  name: 'WebPages',
  injectModels: ['Store'],
  components: {
    ImageUploadDialog
  },
  data() {
    return {
      selectedId: undefined,
      items_: this.Store.web,
      pageThumbnailAspectRatio: 2.1
    }
  },
  computed: {
    selectedItem() {
      return this.getItem(this.selectedId)
    },
    selectedItemPage() {
      return this.selectedItem['.']
    },
    items() {
      function proc(children, parentUrl) {
        return Object.entries(children)
          .filter(([k]) => k !== '.')
          .map(([k, v]) => {
            const url = parentUrl ? `${parentUrl}/${k}` : k
            return {
              id: url,
              name: k,
              children: proc(v, url),
              page: v['.']
            }
          })
          .sort((a, b) => a.name > b.name)
      }

      return [{
        id: '/',
        name: 'Root',
        children: proc(this.items_)
      }]
    }
  },
  methods: {
    addChild() {
      let parent = this.getItem(this.selectedId)
      if (parent)
        this.$set(parent, `__${randomId()}`, {})
    },
    getItem(url) {
      if (url == null)
        return
      let item = this.items_

      if (url !== '/') {
        for (let path of url.split('/')) {
          item = item[path]
          if (item == null)
            return
        }
      }

      return item
    },
    onSelectTreeNode(e) {
      this.selectedId = e[0]
    },
    onChangePathName(newName) {
      newName = newName.replace(/(\/|&|\?)/g, '')
      const paths = this.selectedId.split('/')
      this.selectedId = newName

      if (paths.length === 1) {
        this.setChildName('/', paths[0], newName)
      } else if (paths.length > 1) {
        const oldName = paths.pop()
        const parentUrl = paths.join('/')

        this.setChildName(parentUrl, oldName, newName)
        this.selectedId = `${parentUrl}/${newName}`
      }
    },
    setChildName(parentUrl, oldName, newName) {
      if (oldName === newName)
        return

      const parent = this.getItem(parentUrl)
      const child = parent[oldName]

      if (newName != null && parent[newName])
        return // already exists

      if (parent && child) {
        if (newName != null)
          this.$set(parent, newName, child)
        this.$delete(parent, oldName)
      }
    },
    newPage() {
      this.$set(this.selectedItem, '.', {
        title: '',
        description: ''
      })
    },
    deleteSelectedItem() {
      const paths = this.selectedId.split('/')
      this.selectedId = undefined

      if (paths.length === 1) {
        this.$delete(this.items_, paths[0])
      } else if (paths.length > 1) {
        const name = paths.pop()
        const parentUrl = paths.join('/')
        this.$delete(this.getItem(parentUrl), name)
      }
    },
    onClickAddThumbnail() {
      if (!this.selectedItemPage.thumbnail)
        this.$refs.imageUploadDlg.show()
      else
        this.$delete(this.selectedItemPage, 'thumbnail')
    }
  }
}
</script>
